import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Drawer from "../../components/ui/Drawer";
import { ButtonGroup, Button } from "../../components/ui/buttons/Button";

import "./file-upload.scss";
import WalletService, { requestPayment } from "../../api/wallet-service";
import { WALLET_API_URL } from "../../api/urlMetaData";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "react-feather";
import { useLocale } from "../../hooks/useLocale";

const getTaggedClients = async (clientHashId) => {
  return await WalletService.post(WALLET_API_URL.TAGGED_TRANSPORTERS, {
    clientHashId,
  });
};

const TransporterPayments = ({ id, title, onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const currentUser = useSelector((s) => s.auth.currentUser);
  const currentProgram = useSelector((s) => s.global.currentProgram);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const { localize } = useLocale(["cards"]);

  useEffect(() => {
    reset();
  }, []);

  // fetch tagged client details
  const taggedClientsQuery = useQuery(
    ["cards/taggedclients"],
    () => getTaggedClients(currentUser.clientHashId),
    {
      retry: false,
      refetchInterval: 0,
    }
  );

  if (taggedClientsQuery.isSuccess && !taggedClientsQuery.data?.data?.body) {
    onClose();
    toast("No Clients Tagged!", {
      type: "warning",
    });
  }

  const handleFormSubmit = (formData) => {
    const data = new FormData();
    data.append("programHashId", currentProgram);
    data.append("requesterUserHashId", currentUser.userHashId);

    data.append("orderId", formData.orderId);
    data.append("amount", formData.amount);
    data.append("currencyCode", formData.currencyCode);
    data.append("cashReceiptVoucher", formData.cashReceiptVoucher);

    // tagged client details
    const taggedClient = taggedClientsQuery.data.data.body.find(
      (client) => client.taggedClientHashId === formData.taggedClient
    );

    data.append("taggedClientHashId", taggedClient.taggedClientHashId);
    data.append("taggedClientUserHashId", taggedClient.taggedClientUserHashId);
    data.append("taggedClientName", taggedClient.taggedClientName);

    if (formData.paymentReceiptFile[0])
      data.append("paymentReceiptFile", formData.paymentReceiptFile[0]);

    // Sending Payment Request
    setLoading(true);
    requestPayment(data)
      .then((res) => {
        toast(res.data.message, {
          type: "success",
        });
        setLoading(false);
        reset();
        onClose();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    const docs = Object.values(files).map((file) => URL.createObjectURL(file));
    setDocuments([...docs]);
  };

  const clearUploads = () => {
    setValue("paymentReceiptFile", "");
    setDocuments([]);
  };

  const removeUploadedFile = (key) => {
    const removedFiles = documents.filter((file) => file !== key);
    setDocuments(removedFiles);
  };

  return (
    <>
      <Drawer title={title} id={id} onClose={onClose}>
        <>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="row">
              {/* Select Transporter */}
              <div className="col-sm-12 col-md-12 col-lg-12">
                <label
                  for="select-vendor"
                  className="form-label required text-capitalize"
                >
                  {localize("transporterPayments.form.selectVendor")}
                </label>
                <div className="mb-3 input-group">
                  <select
                    id="select-vendor"
                    className={`form-select ${errors.orderId && "is-invalid"}`}
                    {...register("taggedClient", {
                      required: {
                        value: true,
                        message: "Please Select Vendor ",
                      },
                      disabled: taggedClientsQuery.isLoading,
                    })}
                  >
                    {taggedClientsQuery.isLoading && (
                      <option>Fetching Clients..</option>
                    )}

                    <option value={""}>Select Vendor</option>
                    {taggedClientsQuery.data?.data?.body.map((item) => {
                      return (
                        <>
                          <option value={item.taggedClientHashId}>
                            {item.taggedClientName}
                          </option>
                        </>
                      );
                    })}
                  </select>
                  <div className="invalid-feedback">
                    {errors.taggedClient && errors.taggedClient.message}
                  </div>
                </div>
              </div>

              {!taggedClientsQuery.isLoading ? (
                <>
                  {/* Enter Order id */}
                  <div className="col-sm-12 col-md-12 col-lg-6">
                    <div className="mb-3">
                      <label
                        for="requestAmount"
                        className="form-label required text-capitalize"
                      >
                        {localize("transporterPayments.form.orderId")}
                      </label>
                      <input
                        type="text"
                        id="requestAmount"
                        className={`form-control ${
                          errors.orderId && "is-invalid"
                        }`}
                        step=".01"
                        {...register("orderId", {
                          required: {
                            value: true,
                            message: "Please Enter Order ID",
                          },
                        })}
                      />
                      <div className="invalid-feedback">
                        {errors.orderId && errors.orderId.message}
                      </div>
                    </div>
                  </div>

                  {/* Cash Receipt Voucher. */}
                  <div className="col-sm-12 col-md-12 col-lg-6">
                    <div className="mb-3">
                      <label
                        for="voucher"
                        className="form-label required text-capitalize"
                      >
                        {localize("transporterPayments.form.cashReceipt")}
                      </label>
                      <input
                        type="text"
                        id="voucher"
                        className={`form-control ${
                          errors.cashReceiptVoucher && "is-invalid"
                        }`}
                        {...register("cashReceiptVoucher", {
                          required: {
                            value: true,
                            message: "Please Enter Cash Receipt Voucher",
                          },
                        })}
                      />
                      <div className="invalid-feedback">
                        {errors.cashReceiptVoucher &&
                          errors.cashReceiptVoucher.message}
                      </div>
                    </div>
                  </div>

                  {/* Select Currency */}
                  <div className="col-sm-12 col-md-12 col-lg-6">
                    <div className="mb-3">
                      <label
                        for="requestCurrnecy"
                        className="form-label required text-capitalize"
                      >
                        {localize("transporterPayments.form.currency")}
                      </label>
                      <select
                        id="requestCurrnecy"
                        className="form-select"
                        defaultValue={"AED"}
                        {...register("currencyCode", {
                          required: true,
                        })}
                      >
                        <option value="AED">AED</option>
                      </select>
                    </div>
                  </div>

                  {/* Enter Amount */}
                  <div className="col-sm-12 col-md-12 col-lg-6">
                    <div className="mb-3">
                      <label
                        for="requestAmount"
                        className="form-label required text-capitalize"
                      >
                        {localize("transporterPayments.form.amount")}
                      </label>
                      <input
                        type="text"
                        id="requestAmount"
                        className={`form-control ${
                          errors.amount && "is-invalid"
                        }`}
                        {...register("amount", {
                          required: {
                            value: true,
                            message: "Please Enter Amount",
                          },
                          pattern: {
                            value: /^(?!0(?:\.0{1,2})?$)\d+(\.\d{1,2})?$/,
                            message:
                              "Only positive numbers (up to 2 decimal places are allowed)",
                          },
                        })}
                      />
                      <div className="invalid-feedback">
                        {errors.amount && errors.amount.message}
                      </div>
                    </div>
                  </div>

                  {/*  =======  Upload DOcuments ======= */}
                  {/* Upload Invoice */}
                  <div className="col-sm-12 col-md-12 col-lg-12">
                    <div className="uploads alert alert-light">
                      <div className="d-flex justify-content-between">
                        <h6 className="text-capitalize">
                          {localize("transporterPayments.form.attachment")}
                        </h6>

                        {/* Clear Uploaded Docs */}
                        {documents.length ? (
                          <div>
                            <span
                              className="text-danger"
                              onClick={clearUploads}
                            >
                              X clear
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <div className="file-wrapper">
                        {/* View Attached Docs */}
                        {documents.map((file, index) => {
                          return (
                            <>
                              <div
                                className="file"
                                key={file}
                                data-aos="fade-right"
                                data-aos-delay={100 * index}
                                // onClick={() => removeUploadedFile(file)}
                              >
                                <img
                                  className="img-fluid"
                                  src={file}
                                  alt="Payment Receipt Document"
                                />
                              </div>
                            </>
                          );
                        })}
                        {/* Upload File Input */}
                        <div className={`file-upload`}>
                          <input
                            type="file"
                            {...register("paymentReceiptFile", {
                              required: {
                                value: false,
                                message: "Please Attach Document",
                              },
                              onChange: (e) => {
                                handleFileUpload(e);
                              },
                            })}
                          />
                          <p>
                            <Plus />
                            <br />
                            <span className="fw-bold">Upload File</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {errors.paymentReceiptFile && (
                      <div className="alert alert-danger">
                        {errors.paymentReceiptFile.message}
                      </div>
                    )}

                    <div className="alert alert-warning p-2">
                      {localize("transporterPayments.form.note")}
                    </div>
                  </div>
                  <ButtonGroup>
                    <Button
                      loading={loading}
                      className="btn btn-primary"
                      text={localize("transporterPayments.form.submitButton")}
                    />
                    <Button
                      className="btn btn-light"
                      text={localize("common.buttons.cancel")}
                      onClick={onClose}
                    />
                  </ButtonGroup>
                </>
              ) : null}
            </div>
          </form>
        </>
      </Drawer>
    </>
  );
};

export default TransporterPayments;
